// Recycler fleet: last reported truck positions and the day's route runs, built from
// the shared story (trucks, drivers, routes, collection points, generated runs).

import {
  DEMO_RECYCLER_ORG_ID,
  DEMO_TRUCKS,
  DEMO_ROUTES,
  HOUR_MS,
  collectionPointById,
  daysAgo,
  driverById,
  facilityById,
  generateRouteRuns,
  hoursAgo,
  iso,
  isoDate,
  naiveIso,
  round,
  runPosition,
  supplierById,
  ids,
  type DemoRouteRun,
  type DemoTruck,
} from '../core';
import type {
  FacilitySnapshot,
  OpsRouteRun,
  OpsRouteRunStop,
  PointSnapshot,
  TruckStatus,
  TruckTrackingItem,
} from '../../types/fleet.type';
import type { TruckLocation } from '../../types/dashboard.type';
import { jitter, naive } from './helpers';

let runs: DemoRouteRun[] = generateRouteRuns();

export function resetFleet(): void {
  runs = generateRouteRuns();
}

export function allRouteRuns(): DemoRouteRun[] {
  return runs;
}

/** Average SAR/kg paid at the kerb for mixed route collections (OCC-heavy). */
const ROUTE_PRICE_SAR_PER_KG = 0.52;

function facilitySnapshot(facilityId: string): FacilitySnapshot | null {
  const f = facilityById(facilityId);
  if (!f) return null;
  return {
    id: f.id,
    name: f.name_en,
    type: f.facility_type,
    address: f.address,
    city: f.city,
    latitude: f.lat,
    longitude: f.lng,
  };
}

function pointSnapshot(pointId: string): PointSnapshot {
  const p = collectionPointById(pointId);
  if (!p) return { id: pointId };
  const supplier = supplierById(p.supplier_id);
  return {
    id: p.id,
    supplier_id: p.supplier_id,
    supplier_name: supplier?.name_en ?? null,
    name: p.name_en,
    address: p.address,
    city: p.city,
    latitude: p.lat,
    longitude: p.lng,
    google_maps_url: `https://www.google.com/maps?q=${p.lat},${p.lng}`,
    contact_name: supplier?.contact_name ?? null,
    contact_phone: supplier?.contact_phone ?? null,
  };
}

function runNumber(run: DemoRouteRun): number {
  return Number.parseInt(run.id.slice(-6), 16);
}

function toOpsRouteRun(run: DemoRouteRun, sequence: number): OpsRouteRun {
  const route = DEMO_ROUTES.find((r) => r.id === run.route_id);
  const truck = DEMO_TRUCKS.find((t) => t.id === run.truck_id);
  const driver = run.driver_id ? driverById(run.driver_id) : undefined;
  const n = runNumber(run);
  const stops: OpsRouteRunStop[] = run.stops.map((stop) => {
    const point = collectionPointById(stop.collection_point_id);
    const completedAt = stop.arrived_at && stop.status === 'completed'
      ? naiveIso(new Date(Date.parse(stop.arrived_at) + 12 * 60000))
      : null;
    return {
      id: ids.schedule(n * 10 + stop.seq),
      collection_point_id: stop.collection_point_id,
      supplier_id: point?.supplier_id ?? '',
      sequence: stop.seq,
      status: stop.status,
      pickup_order_id: null,
      arrived_at: naive(stop.arrived_at),
      completed_at: completedAt,
      cancelled_at: null,
      cancellation_reason: null,
      point_snapshot: pointSnapshot(stop.collection_point_id),
      weight_kg: stop.collected_kg,
      price_amount: stop.collected_kg !== null ? round(stop.collected_kg * ROUTE_PRICE_SAR_PER_KG, 2) : null,
      order_status: stop.status === 'completed' ? 'completed' : 'pending',
      handed_off: false,
    };
  });
  const counts: Record<string, number> = {};
  for (const stop of stops) counts[stop.status] = (counts[stop.status] ?? 0) + 1;
  const facility = route ? facilitySnapshot(route.facility_id) : null;
  return {
    id: run.id,
    organization_id: DEMO_RECYCLER_ORG_ID,
    route_id: run.route_id,
    route_name: route?.name_en ?? null,
    truck_id: run.truck_id,
    truck_name: truck?.plate_number ?? null,
    driver_id: run.driver_id,
    driver_name: driver?.full_name ?? null,
    run_date: run.run_date,
    sequence,
    status: run.status,
    clock_in_at: naive(run.started_at),
    clock_out_at: naive(run.finished_at),
    offload_at: run.finished_at ? naiveIso(new Date(Date.parse(run.finished_at) - 20 * 60000)) : null,
    planned_geometry: { type: 'LineString', coordinates: run.geometry.map((p): [number, number] => [p[0], p[1]]) },
    planned_distance_km: run.distance_km,
    planned_duration_seconds: route ? route.est_duration_min * 60 : null,
    route_snapshot: {
      route_id: run.route_id,
      name: route?.name_en ?? null,
      city: facility?.city ?? null,
      start_facility: facility,
      end_facility: facility,
      collection_points: run.stops.map((s) => pointSnapshot(s.collection_point_id)),
    },
    stop_status_counts: counts,
    stops,
  };
}

/** Local calendar date of the browser as 'YYYY-MM-DD'. */
function localKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Route runs of one day. The UI asks with the browser's local date; the story's
 * days are UTC, so the request is mapped by its offset from "today".
 */
export function routeRunsForDay(orgId: string | null | undefined, runDate: string | undefined): OpsRouteRun[] {
  if (orgId !== DEMO_RECYCLER_ORG_ID) return [];
  const todayLocal = localKey(new Date());
  const requested = runDate && /^\d{4}-\d{2}-\d{2}$/.test(runDate) ? runDate : todayLocal;
  const diffDays = Math.round(
    (Date.UTC(+requested.slice(0, 4), +requested.slice(5, 7) - 1, +requested.slice(8, 10)) -
      Date.UTC(+todayLocal.slice(0, 4), +todayLocal.slice(5, 7) - 1, +todayLocal.slice(8, 10))) /
      (24 * HOUR_MS),
  );
  const storyDay = isoDate(daysAgo(-diffDays));
  return runs
    .filter((r) => r.run_date === storyDay)
    .map((r, i) => ({ ...toOpsRouteRun(r, i + 1), run_date: requested }));
}

export function todaysRuns(): DemoRouteRun[] {
  const today = isoDate(daysAgo(0));
  return runs.filter((r) => r.run_date === today);
}

// ── Truck positions ─────────────────────────────────────────────────────────

function truckStatus(truck: DemoTruck): TruckStatus {
  return truck.status;
}

/** Where each truck last reported from, and when (minutes ago). */
function lastReport(truck: DemoTruck, index: number, now: Date): { lat: number; lng: number; at: Date } {
  const facility = facilityById(truck.facility_id);
  const base = facility ? { lat: facility.lat, lng: facility.lng } : { lat: 24.7136, lng: 46.6753 };
  const { dLat, dLng } = jitter(index, 0.0009);
  const today = todaysRuns().filter((r) => r.truck_id === truck.id);
  const live = today.find((r) => r.status === 'in_progress');
  if (live) {
    // On the road: position follows the live run, reported a minute ago.
    return { ...runPosition(live, now), at: new Date(now.getTime() - 60_000) };
  }
  if (truck.status === 'loading') {
    // Loading at the Exit 10 distribution centre's dock.
    const point = collectionPointById(ids.collectionPoint(4));
    return { lat: (point?.lat ?? base.lat) + 0.0002, lng: (point?.lng ?? base.lng) - 0.0002, at: new Date(now.getTime() - 4 * 60_000) };
  }
  const done = today.find((r) => r.status === 'completed');
  if (done?.finished_at) {
    return { lat: base.lat + dLat, lng: base.lng + dLng, at: new Date(Date.parse(done.finished_at)) };
  }
  if (truck.status === 'maintenance') {
    return { lat: base.lat + dLat, lng: base.lng + dLng, at: hoursAgo(74) };
  }
  const pending = today.find((r) => r.status === 'pending');
  if (pending) {
    return { lat: base.lat + dLat, lng: base.lng + dLng, at: new Date(now.getTime() - 18 * 60_000) };
  }
  // Parked at the depot; last ping a while ago.
  return { lat: base.lat + dLat, lng: base.lng + dLng, at: new Date(now.getTime() - (45 + index * 25) * 60_000) };
}

export function truckTracking(orgId: string | null | undefined): TruckTrackingItem[] {
  if (orgId !== DEMO_RECYCLER_ORG_ID) return [];
  const now = new Date();
  return DEMO_TRUCKS.map((truck, index) => {
    const report = lastReport(truck, index, now);
    const driver = truck.driver_id ? driverById(truck.driver_id) : undefined;
    return {
      truck: {
        id: truck.id,
        name: truck.plate_number,
        capacity: truck.capacity_kg,
        status: truckStatus(truck),
        organization_id: DEMO_RECYCLER_ORG_ID,
        driver_id: truck.driver_id,
        latitude: round(report.lat, 6),
        longitude: round(report.lng, 6),
        data: {
          plate_number: truck.plate_number,
          make: truck.make,
          model: truck.model,
          year: truck.year,
          fuel_type: truck.fuel_type,
          facility_id: truck.facility_id,
          city: truck.city,
        },
        created_at: naiveIso(daysAgo(520 - index * 40)),
        updated_at: naiveIso(report.at),
      },
      current_driver: driver ? { id: driver.id, username: driver.email, phone: driver.phone } : null,
    };
  });
}

/** Dashboard `active_trucks` (recycler): last positions with today's load. */
export function activeTruckLocations(orgId: string | null | undefined): TruckLocation[] {
  return truckTracking(orgId).map((item, index) => {
    const truck = DEMO_TRUCKS[index];
    const live = todaysRuns().find((r) => r.truck_id === truck.id && r.status === 'in_progress');
    const load = live ? live.stops.reduce((s, stop) => s + (stop.collected_kg ?? 0), 0) : 0;
    return {
      truck_id: item.truck.id,
      truck_name: item.truck.name,
      latitude: item.truck.latitude ?? 0,
      longitude: item.truck.longitude ?? 0,
      last_updated: iso(new Date(Date.parse(`${item.truck.updated_at}Z`))),
      status: truck.status === 'maintenance' ? 'maintenance' : truck.status === 'available' ? 'idle' : 'active',
      current_load: load,
      capacity: truck.capacity_kg,
    };
  });
}
